import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  Grid,
  Stack,
  Checkbox,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Divider,
} from '@mui/material';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded';

import { sortProjectRows } from '../../../utils/functions/sortProjects';
import { ACCENT_COLOR } from './constants';

import { getBankLabel } from '../../../constants/thaiBanks';
// ฟิลด์ "รายละเอียดค่าใช้จ่าย" ของรายการเบิกประจำ — สร้างแยกจาก
// components/Forms/ExpensesApplyForm.js (ฟอร์มของ module รายการเบิกจ่ายปกติ) ทั้งหมดตามที่ขอ
// เอาไว้ปรับ layout/สไตล์อิสระโดยไม่กระทบฟอร์มที่ใช้ร่วมกันอยู่ทั่วทั้ง module เดิม — ใช้ 2 ที่:
// ฟอร์ม template (showAmount=false — template ไม่เก็บยอด เพราะแต่ละงวดไม่เท่ากัน) และฟอร์มสร้างรายการเบิก
// จาก template (showAmount=true — กรอกยอด/VAT/หัก ณ ที่จ่าย ของงวดนั้น base_amount → vat_amount/price/
// withholding_tax/net_price)
export default function RecurringExpenseFields({
  expenses,
  control,
  Controller,
  setValue,
  watch,
  projects,
  projectSelect,
  setProjectSelect,
  budgets,
  budgetSelect,
  setBudgetSelect,
  customer,
  customerSelect,
  setCustomerSelect,
  showAmount = true,
  nameHelper = null,
  // { value, onChange(text) } — ให้ parent คุมข้อความที่โชว์ในช่อง "รายการ" เอง (ฟอร์มสร้างรายการเบิกใช้
  // ผูก {ยอด} ให้ตัวเลขเปลี่ยนตามยอดจ่ายสุทธิทันที) ไม่ส่ง = ใช้ค่าจาก react-hook-form ตามปกติ
  nameControl = null,
}) {
  const [hasTax, setHasTax] = useState(false);
  const [includesVat, setIncludesVat] = useState(false);
  const [taxRate, setTaxRate] = useState(3);

  useEffect(() => {
    if (expenses) {
      setHasTax(!!expenses.withholding_tax || expenses.tax_3);
      setIncludesVat(!!expenses.includes_vat);
      if (expenses.withholding_tax_rate) setTaxRate(expenses.withholding_tax_rate);
    } else {
      setHasTax(false);
      setIncludesVat(false);
      setTaxRate(3);
    }
  }, [expenses]);

  const safeWatch = (name) => (typeof watch === 'function' ? watch(name) : null);
  const baseAmountValue = safeWatch('base_amount');

  useEffect(() => {
    if (!showAmount) return;
    if (baseAmountValue === null || baseAmountValue === undefined || baseAmountValue === '' || Number.isNaN(parseFloat(baseAmountValue))) {
      setValue('vat_amount', 0);
      setValue('price', 0);
      setValue('withholding_tax', 0);
      setValue('net_price', 0);
      return;
    }
    const basePrice = parseFloat(baseAmountValue);
    let vatAmount = 0;
    let totalPrice = basePrice;
    if (includesVat) {
      vatAmount = basePrice * 0.07;
      totalPrice = basePrice + vatAmount;
    }
    setValue('vat_amount', vatAmount);
    setValue('price', totalPrice);

    let withholdingTax = 0;
    if (hasTax) {
      withholdingTax = basePrice * (taxRate / 100);
      setValue('withholding_tax', withholdingTax);
      setValue('withholding_tax_rate', taxRate);
      setValue('tax_3', true);
    } else {
      setValue('withholding_tax', 0);
      setValue('withholding_tax_rate', 0);
      setValue('tax_3', false);
    }
    setValue('net_price', totalPrice - withholdingTax);
  }, [showAmount, baseAmountValue, includesVat, hasTax, taxRate, setValue]);

  const handleProjectChange = (val) => {
    const each = _.find(projects?.rows, { _id: val?._id });
    setValue('project', each?._id);
    setProjectSelect(each);
    setValue('budget', null);
    if (typeof setBudgetSelect === 'function') setBudgetSelect(null);
  };

  const handleBudgetChange = (val) => {
    const each = _.find(budgets?.rows, { _id: val?._id });
    setValue('budget', each?._id);
    setBudgetSelect(each);
  };

  const handleCustomerChange = (val) => {
    const each = _.find(customer?.rows, { _id: val?._id });
    setValue('customer', each?._id);
    setValue('payee.bank', each?.bank?.bank_name || '');
    setValue('payee.name', each?.bank?.account_name || '');
    setValue('payee.account_number', each?.bank?.account_number || '');
    if (typeof setCustomerSelect === 'function') setCustomerSelect(each);
  };

  const sectionLabel = (icon, text) => (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
      {icon}
      <Typography variant="subtitle2" fontWeight={700}>{text}</Typography>
    </Stack>
  );

  return (
    <Box>
      {sectionLabel(<AssignmentRoundedIcon fontSize="small" sx={{ color: ACCENT_COLOR }} />, 'โครงการ / งบประมาณ')}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={7}>
          <Controller
            name="project"
            control={control}
            render={({ field }) => (
              <Autocomplete
                {...field}
                disablePortal
                size="small"
                value={projectSelect || null}
                options={sortProjectRows(projects?.rows)}
                getOptionLabel={(option) => (typeof option === 'string' ? option : `${option.project_number}|ลูกค้า: ${option.customer} | ${option.name}`)}
                isOptionEqualToValue={(option, value) => option?._id === value?._id}
                onChange={(e, val) => handleProjectChange(val)}
                renderInput={(params) => <TextField {...params} label="โครงการ" size="small" fullWidth />}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={5}>
          <Controller
            name="budget"
            control={control}
            render={({ field }) => (
              <Autocomplete
                {...field}
                key={projectSelect?._id || 'no-project'}
                disablePortal
                size="small"
                value={budgetSelect || null}
                disabled={!projectSelect}
                options={(budgets?.rows || []).filter((b) => !projectSelect?._id || String(b.project_id) === String(projectSelect._id))}
                getOptionLabel={(option) => (typeof option === 'string' ? option : `${option.prefix || ''}${option.budget_number} | ${option.name}`)}
                noOptionsText="ไม่มีงบประมาณสำหรับโครงการนี้"
                isOptionEqualToValue={(option, value) => option?._id === value?._id}
                onChange={(e, val) => handleBudgetChange(val)}
                renderInput={(params) => <TextField {...params} label="งบประมาณ" size="small" fullWidth />}
              />
            )}
          />
        </Grid>
      </Grid>

      {sectionLabel(<ReceiptLongRoundedIcon fontSize="small" sx={{ color: ACCENT_COLOR }} />, 'รายละเอียดค่าใช้จ่าย')}
      <Grid container spacing={1.5} sx={{ mb: 0.5 }}>
        <Grid item xs={12} sm={4}>
          <Controller
            name="expenses_type"
            control={control}
            defaultValue="REFUND"
            render={({ field }) => (
              <FormControl fullWidth size="small">
                <InputLabel id="recur-expenses-type-label">ประเภทการเบิก</InputLabel>
                <Select labelId="recur-expenses-type-label" label="ประเภทการเบิก" {...field}>
                  <MenuItem value="PAY">เบิกเพื่อจ่าย</MenuItem>
                  <MenuItem value="REFUND">เบิกคืน</MenuItem>
                </Select>
              </FormControl>
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name="name"
            control={control}
            defaultValue={expenses ? expenses?.name : ''}
            rules={{ required: 'กรุณาระบุรายการ', maxLength: { value: 255, message: 'ความยาวต้องไม่เกิน 255' } }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                {...(nameControl ? { value: nameControl.value, onChange: (e) => nameControl.onChange(e.target.value) } : {})}
                label="รายการ"
                size="small"
                fullWidth
                multiline
                minRows={2}
                maxRows={4}
                error={!!error}
                helperText={error ? error.message : (nameHelper || ' ')}
              />
            )}
          />
        </Grid>

        {showAmount && (
        <Grid item xs={12}>
          <Box
            sx={{
              p: 1.75,
              borderRadius: 2,
              bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(124,108,240,0.08)' : 'rgba(124,108,240,0.05)'),
              border: '1px solid',
              borderColor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(124,108,240,0.25)' : 'rgba(124,108,240,0.18)'),
            }}
          >
            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} sm={4}>
                <Controller
                  name="base_amount"
                  control={control}
                  defaultValue={expenses ? (expenses?.base_amount ?? (expenses?.includes_vat ? (expenses?.price - (expenses?.vat_amount || 0)) : (expenses?.net_price ?? expenses?.price))) : ''}
                  rules={{ required: 'กรุณาระบุจำนวนเงิน' }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="ยอดก่อน VAT"
                      size="small"
                      type="number"
                      fullWidth
                      error={!!error}
                      InputProps={{ startAdornment: <InputAdornment position="start">฿</InputAdornment> }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <FormControlLabel
                  control={(
                    <Checkbox
                      size="small"
                      checked={includesVat}
                      onChange={(e) => { setIncludesVat(e.target.checked); setValue('includes_vat', e.target.checked); }}
                    />
                  )}
                  label={<Typography variant="body2">VAT 7% (+{Number(safeWatch('vat_amount') || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })})</Typography>}
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <FormControlLabel
                    control={<Checkbox size="small" checked={hasTax} onChange={(e) => setHasTax(e.target.checked)} />}
                    label={<Typography variant="body2">หัก ณ ที่จ่าย</Typography>}
                    sx={{ mr: 0 }}
                  />
                  {hasTax && (
                    <TextField
                      size="small"
                      type="number"
                      value={taxRate}
                      onChange={(e) => setTaxRate(Number(e.target.value))}
                      sx={{ width: 64 }}
                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                    />
                  )}
                </Stack>
              </Grid>
            </Grid>
            <Divider sx={{ my: 1 }} />
            <Stack direction="row" justifyContent="space-between" alignItems="baseline">
              <Typography variant="body2" color="text.secondary">ยอดจ่ายจริงสุทธิ</Typography>
              <Typography variant="h6" fontWeight={700} sx={{ color: ACCENT_COLOR }}>
                {Number(safeWatch('net_price') || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })} บาท
              </Typography>
            </Stack>
          </Box>
        </Grid>
        )}
      </Grid>

      {sectionLabel(<AccountBalanceRoundedIcon fontSize="small" sx={{ color: ACCENT_COLOR }} />, 'ผู้รับเงิน')}
      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={5}>
          <Controller
            name="customer"
            control={control}
            render={({ field }) => (
              <Autocomplete
                {...field}
                disablePortal
                size="small"
                value={customerSelect || null}
                options={customer?.rows || []}
                getOptionLabel={(option) => option?.name || ''}
                isOptionEqualToValue={(option, value) => option?._id === value?._id}
                onChange={(e, val) => handleCustomerChange(val)}
                renderInput={(params) => <TextField {...params} label="ชื่อผู้รับเงิน" size="small" fullWidth />}
              />
            )}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <Controller
            name="payee.bank"
            control={control}
            defaultValue=""
            render={({ field }) => <TextField {...field} value={getBankLabel(field.value)} label="ธนาคาร" size="small" fullWidth disabled />}
          />
        </Grid>
        <Grid item xs={6} sm={4}>
          <Controller
            name="payee.account_number"
            control={control}
            defaultValue=""
            render={({ field }) => <TextField {...field} label="เลขที่บัญชี" size="small" fullWidth disabled />}
          />
        </Grid>
      </Grid>
    </Box>
  );
}
